import React, { useEffect, useMemo, useRef, useState } from 'react';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { LoadingButton } from '@mui/lab';
import {
   Box,
   Button,
   Card,
   CardContent,
   Container,
   CssBaseline,
   FormControl,
   FormControlLabel,
   FormLabel,
   InputLabel,
   LinearProgress,
   MenuItem,
   Radio,
   RadioGroup,
   Select,
   Tooltip,
   Typography,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import api from 'api';
import { useSignalRState } from 'contexts/SignalRContext';
import { useNavigate } from 'react-router-dom';
import {
   resetDecodedImageData,
   selectImageView,
   setCoatThickness,
   setCoatType,
   setSelectedLayer as setSelectedLayerRedux,
   stateOfGeneralSlice,
   updateSettings,
} from 'state/general/general.slice';
import { type CoatThickness, type CoatType, JOB_STATUS } from 'state/general/general.types';
import { useAppDispatch, useAppSelector } from 'store/hooks';

type UploadLayout = 'separate' | 'combined';

function firstPdfFromFileList(files: FileList | null): File | null {
   if (!files?.length) return null;
   for (let i = 0; i < files.length; i++) {
      const f = files[i];
      if (f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')) {
         return f;
      }
   }
   return null;
}

function UploadDropPlaceholder({ detail }: { detail: string }): JSX.Element {
   return (
      <Box
         sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            width: '100%',
            py: 0.25,
         }}
      >
         <CloudUploadIcon color='action' sx={{ fontSize: 28 }} />
         <Box sx={{ width: '100%', alignSelf: 'stretch' }}>
            <Typography variant='body2' sx={{ fontWeight: 500, textAlign: 'center', lineHeight: 1.35 }}>
               Drop your files here or{' '}
               <Box component='span' sx={{ textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                  Browse
               </Box>
            </Typography>
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', pl: 3, mt: 0.5 }}>
               {detail}
            </Typography>
         </Box>
      </Box>
   );
}

const JobSelection = (): JSX.Element => {
   const navigate = useNavigate();
   const dispatch = useAppDispatch();
   const { settings } = useAppSelector(stateOfGeneralSlice);
   const { connectionId } = settings;
   const [uploadLayout, setUploadLayout] = useState<UploadLayout>('separate');
   const [cmykFile, setCmykFile] = useState<File | null>(null);
   const [scodixFile, setScodixFile] = useState<File | null>(null);
   const [cmykFileName, setCmykFileName] = useState('');
   const [scodixFileName, setScodixFileName] = useState('');
   const [combinedFile, setCombinedFile] = useState<File | null>(null);
   const [combinedFileName, setCombinedFileName] = useState('');
   const [isProcessing, setIsProcessing] = useState(false);
   const [progress, setProgress] = useState(0);
   const [selectedCoatType, setSelectedCoatType] = useState<CoatType>('gold');
   const [selectedThickness, setSelectedThickness] = useState<CoatThickness>('mid');
   const [selectedLayer, setSelectedLayer] = useState<string>('');
   const [sessionUserId, setSessionUserId] = useState<string | null>(() => {
      try {
         return sessionStorage.getItem('userId');
      } catch (e) {
         return null;
      }
   });
   const [isDebugEnabled, setIsDebugEnabled] = useState<boolean>(() => {
      try {
         return localStorage.getItem('debug_3d') === 'true';
      } catch (e) {
         return false;
      }
   });

   const coatTypeToImageView: Record<CoatType, 'Preview' | 'sense' | 'silver foil'> = {
      varnish: 'sense',
      gold: 'Preview',
      silver: 'silver foil',
      silverBlue: 'Preview',
      bronze: 'Preview',
      roseGold: 'Preview',
      champagne: 'Preview',
      emeraldGreen: 'Preview',
      burgundy: 'Preview',
      lavender: 'Preview',
      royalPurple: 'Preview',
      black: 'Preview',
   };

   const isMountedRef = useRef(true);
   useEffect(() => {
      return () => {
         isMountedRef.current = false;
      };
   }, []);

   useEffect(() => {
      const handleStorage = (event: StorageEvent) => {
         if (event.key && event.key !== 'debug_3d') return;
         try {
            setIsDebugEnabled(localStorage.getItem('debug_3d') === 'true');
         } catch (err) {
            setIsDebugEnabled(false);
         }
      };

      window.addEventListener('storage', handleStorage);
      return () => {
         window.removeEventListener('storage', handleStorage);
      };
   }, []);

   useEffect(() => {
      const updateUserIdFromSession = () => {
         try {
            setSessionUserId(sessionStorage.getItem('userId'));
         } catch (e) {
            setSessionUserId(null);
         }
      };

      const handleSessionUserEvent = (_event: Event) => {
         updateUserIdFromSession();
      };

      window.addEventListener('session-user-updated', handleSessionUserEvent);
      window.addEventListener('storage', handleSessionUserEvent);

      return () => {
         window.removeEventListener('session-user-updated', handleSessionUserEvent);
         window.removeEventListener('storage', handleSessionUserEvent);
      };
   }, []);

   useEffect(() => {
      dispatch(setCoatType('gold'));
      dispatch(setCoatThickness('mid'));
      dispatch(selectImageView('Preview'));
   }, [dispatch]);

   const processFileName = (originalName: string, maxLength = 100): string => {
      const sanitizedName = originalName.split(/[/\\]/).pop() ?? originalName;

      const lastDotIndex = sanitizedName.lastIndexOf('.');
      let baseName = sanitizedName;
      let extension = '';

      if (lastDotIndex !== -1) {
         baseName = sanitizedName.substring(0, lastDotIndex);
         extension = sanitizedName.substring(lastDotIndex + 1);
      }

      const availableForName = maxLength - (extension ? extension.length + 1 : 0);
      const finalNameWithoutExt = availableForName <= 0 ? '' : baseName.substring(0, availableForName);

      return extension ? `${finalNameWithoutExt}.${extension}` : finalNameWithoutExt;
   };

   // Add utility function for display truncation
   const truncateForDisplay = (fileName: string, maxLength = 25): string => {
      if (fileName.length <= maxLength) return fileName;
      const extensionIndex = fileName.lastIndexOf('.');
      if (extensionIndex === -1) {
         return `${fileName.substring(0, maxLength - 3)}...`;
      }
      const name = fileName.substring(0, extensionIndex);
      const extension = fileName.substring(extensionIndex);
      const availableForName = maxLength - extension.length - 3; // 3 for "..."
      if (availableForName <= 0) {
         return `...${extension}`;
      }
      return `${name.substring(0, availableForName)}...${extension}`;
   };

   const assignUploadFile = React.useCallback(
      (
         file: File | null,
         fileSetter: React.Dispatch<React.SetStateAction<File | null>>,
         nameSetter: React.Dispatch<React.SetStateAction<string>>,
         options?: { resetCoatOnClear?: boolean },
      ) => {
         const resetCoatOnClear = options?.resetCoatOnClear !== false;
         fileSetter(file);

         if (!file) {
            nameSetter('');
            if (resetCoatOnClear) {
               setSelectedCoatType('gold');
               setSelectedThickness('mid');
               dispatch(setCoatType('gold'));
               dispatch(setCoatThickness('mid'));
               dispatch(selectImageView('Preview'));
            }
            return;
         }

         const processedName = processFileName(file.name);
         nameSetter(processedName);

         const fileForUpload =
            processedName !== file.name ? new File([file], processedName, { type: file.type }) : file;

         (async () => {
            try {
               await api.fileUpload.uploadFile(fileForUpload);
            } catch (e) {
               // eslint-disable-next-line no-console
               console.error('Upload via fileUpload failed', e);
            }
         })();
      },
      [dispatch],
   );

   const handleFileChange =
      (
         fileSetter: React.Dispatch<React.SetStateAction<File | null>>,
         nameSetter: React.Dispatch<React.SetStateAction<string>>,
      ) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
         const file = event.target.files?.[0] ?? null;
         assignUploadFile(file, fileSetter, nameSetter);
      };

   const handleCoatTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value as CoatType;
      setSelectedCoatType(value);
      dispatch(setCoatType(value));
      dispatch(selectImageView(coatTypeToImageView[value]));
   };

   const handleThicknessChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value as CoatThickness;
      setSelectedThickness(value);
      dispatch(setCoatThickness(value));
   };

   const filesReadyToProcess =
      uploadLayout === 'separate' ? Boolean(cmykFile && scodixFile) : Boolean(combinedFile);
   const isCoatingSelectionEnabled = filesReadyToProcess;

   const {
      filesReady,
      error,
      connectionError,
      registrationError,
      progress: progressUpdate,
   } = useSignalRState();
   const hasNoErrors = !error && !connectionError;
   const isUploadDisabled = !!registrationError;

   const cmykDragDepthRef = useRef(0);
   const scodixDragDepthRef = useRef(0);
   const combinedDragDepthRef = useRef(0);
   const [cmykDropActive, setCmykDropActive] = useState(false);
   const [scodixDropActive, setScodixDropActive] = useState(false);
   const [combinedDropActive, setCombinedDropActive] = useState(false);

   const createPdfDropHandlers = (
      fileSetter: React.Dispatch<React.SetStateAction<File | null>>,
      nameSetter: React.Dispatch<React.SetStateAction<string>>,
      dragDepthRef: React.MutableRefObject<number>,
      setDropActive: React.Dispatch<React.SetStateAction<boolean>>,
   ) => ({
      onDragEnter: (e: React.DragEvent) => {
         e.preventDefault();
         e.stopPropagation();
         if (isUploadDisabled) return;
         dragDepthRef.current += 1;
         setDropActive(true);
      },
      onDragLeave: (e: React.DragEvent) => {
         e.preventDefault();
         e.stopPropagation();
         dragDepthRef.current -= 1;
         if (dragDepthRef.current <= 0) {
            dragDepthRef.current = 0;
            setDropActive(false);
         }
      },
      onDragOver: (e: React.DragEvent) => {
         e.preventDefault();
         e.stopPropagation();
      },
      onDrop: (e: React.DragEvent) => {
         e.preventDefault();
         e.stopPropagation();
         dragDepthRef.current = 0;
         setDropActive(false);
         if (isUploadDisabled) return;
         const file = firstPdfFromFileList(e.dataTransfer.files);
         if (file) assignUploadFile(file, fileSetter, nameSetter);
      },
   });

   const cmykDropHandlers = createPdfDropHandlers(
      setCmykFile,
      setCmykFileName,
      cmykDragDepthRef,
      setCmykDropActive,
   );
   const scodixDropHandlers = createPdfDropHandlers(
      setScodixFile,
      setScodixFileName,
      scodixDragDepthRef,
      setScodixDropActive,
   );
   const combinedDropHandlers = createPdfDropHandlers(
      setCombinedFile,
      setCombinedFileName,
      combinedDragDepthRef,
      setCombinedDropActive,
   );

   // Re-enable Start Visualize button when filesReady is received
   useEffect(() => {
      if (filesReady) {
         setIsProcessing(false);
         setProgress(100);
      }
   }, [filesReady]);

   useEffect(() => {
      if (filesReady?.scodixFileData && filesReady.scodixFileData.length > 0) {
         const firstLayer = filesReady.scodixFileData[0]?.Layer;
         // Only update if current selection is invalid or empty
         const isValidSelection = filesReady.scodixFileData.some((layer) => layer.Layer === selectedLayer);
         if (!selectedLayer || !isValidSelection) {
            setSelectedLayer(firstLayer || '');
         }
      } else {
         setSelectedLayer('');
      }
   }, [filesReady]);

   // Compute a valid radio value that always matches one of the options
   const validRadioValue = useMemo(() => {
      if (!filesReady?.scodixFileData || filesReady.scodixFileData.length === 0) {
         return '';
      }
      const isValidValue = filesReady.scodixFileData.some((layer) => layer.Layer === selectedLayer);
      return isValidValue ? selectedLayer : filesReady.scodixFileData[0]?.Layer || '';
   }, [filesReady, selectedLayer]);

   const handleUploadLayoutChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value as UploadLayout;
      setUploadLayout(next);
      if (next === 'combined') {
         setCmykFile(null);
         setCmykFileName('');
         setScodixFile(null);
         setScodixFileName('');
      } else {
         setCombinedFile(null);
         setCombinedFileName('');
      }
   };

   const handleProcess = async () => {
      const separateOk = uploadLayout === 'separate' && cmykFile && scodixFile;
      const combinedOk = uploadLayout === 'combined' && combinedFile;
      if (!separateOk && !combinedOk) return;
      try {
         // Ping SignalR (handled by App listener) before starting processing
         try {
            window.dispatchEvent(new Event('signalr-ping'));
         } catch (e) {
            void e;
         }

         setIsProcessing(true);
         setProgress(0);

         // Reset previous images/state like toolbar upload
         dispatch(resetDecodedImageData());

         // Ensure UI is in idle state (enable toolbar, no upload overlay)
         dispatch(updateSettings({ jobStatus: JOB_STATUS.UPLOADING }));

         const formData = new FormData();
         const files: File[] =
            uploadLayout === 'separate' && cmykFile && scodixFile ? [cmykFile, scodixFile] : [combinedFile!];
         const uploadedFileNames: string[] =
            uploadLayout === 'separate'
               ? [cmykFileName, scodixFileName]
               : [combinedFileName, combinedFileName];

         for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const processedFileName = uploadedFileNames[i] ?? processFileName(file.name);
            const finalFile =
               processedFileName !== file.name
                  ? new File([file], processedFileName, { type: file.type })
                  : file;

            formData.append('file', finalFile);
         }

         // if (process.env.NODE_ENV !== 'development') {
         // Same fingerprint groups all process requests into one Sentry issue (Dedupe disabled in instrument.ts)
         Sentry.captureMessage('User Visualize Process Request', {
            level: 'info',
            fingerprint: ['process-request'],
            extra:
               uploadLayout === 'separate'
                  ? {
                       cmykFile: uploadedFileNames[0] ?? '',
                       scodixFile: uploadedFileNames[1] ?? '',
                       uploadLayout: 'separate',
                    }
                  : {
                       combinedFile: uploadedFileNames[0] ?? '',
                       uploadLayout: 'combined',
                    },
         });
         // }

         const timestamp = Date.now();
         const baseIdentifier =
            (sessionUserId && sessionUserId.trim()) || (connectionId && connectionId.trim()) || 'client';
         const sanitizedClientId = String(baseIdentifier)
            .replace(/[^a-z0-9-_]/gi, '_')
            .replace(/_+/g, '_');
         const ripFileName = `${timestamp}_${sanitizedClientId}.rip`;
         // Combined mode: same filename for both sides (single multi-layer PDF).
         const ripContent = `${uploadedFileNames[0] ?? ''}|${uploadedFileNames[1] ?? ''}`;
         const ripBlob = new Blob([ripContent], { type: 'text/plain' });
         const ripFile = new File([ripBlob], ripFileName, { type: 'text/plain' });

         try {
            await api.fileUpload.uploadFile(ripFile);
         } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Upload via fileUpload failed for RIP file', error);
         }
         //await preview.submit(formData);
         // Don't set isProcessing to false here - wait for filesReady to be received
      } catch (e) {
         // ignore errors here; upstream toasts/logging can handle if needed
         // Reset processing state on error so user can try again
         if (isMountedRef.current) {
            setIsProcessing(false);
         }
      }
   };

   const handleShowPreview = () => {
      if (!filesReady || !validRadioValue) return;

      const matchedLayer = filesReady.scodixFileData?.find((x) => x.Layer === validRadioValue);
      if (!matchedLayer) return;

      try {
         sessionStorage.setItem('previewLayer', validRadioValue);
      } catch (e) {
         // ignore storage errors
      }
      dispatch(setSelectedLayerRedux(validRadioValue));

      navigate('/preview');
   };

   return (
      <Container
         component='main'
         maxWidth='md'
         sx={{
            minHeight: 'calc(100vh - 50px)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            py: 2,
         }}
      >
         <CssBaseline />
         <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Typography variant='h4' component='h1' fontWeight={600} textAlign='center'>
               Upload your design files to begin
            </Typography>
         </Box>

         <Card
            variant='outlined'
            sx={{
               p: 2,
               width: 'min(100%, 620px)',
               mx: 'auto',
               boxSizing: 'border-box',
            }}
         >
            <CardContent>
               <Typography variant='subtitle1' color='text.secondary' mb={2}>
                  {uploadLayout === 'separate'
                     ? 'Upload a CMYK PDF and an embellishment PDF.'
                     : 'Upload one PDF that includes CMYK and embellishment layers.'}
               </Typography>

               <FormControl component='fieldset' sx={{ mb: 3, width: '100%' }} disabled={isUploadDisabled}>
                  <FormLabel component='legend'>File upload mode</FormLabel>
                  <RadioGroup row value={uploadLayout} onChange={handleUploadLayoutChange} sx={{ flexWrap: 'wrap' }}>
                     <FormControlLabel
                        value='separate'
                        control={<Radio />}
                        label='Two PDFs (CMYK + embellishment)'
                     />
                     <FormControlLabel value='combined' control={<Radio />} label='Single multi-layer PDF' />
                  </RadioGroup>
               </FormControl>

               {uploadLayout === 'separate' ? (
                  <Box display='grid' gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={3}>
                     <Box>
                        <Typography variant='subtitle1' fontWeight={600} mb={2}>
                           Upload CMYK design PDF
                        </Typography>
                        <Box
                           component='label'
                           sx={{ display: 'block', cursor: isUploadDisabled ? 'not-allowed' : 'pointer' }}
                        >
                           <Tooltip
                              title={
                                 isUploadDisabled
                                    ? 'Account already connected from another device.'
                                    : cmykFile
                                      ? cmykFileName
                                      : ''
                              }
                              placement='top'
                              arrow
                           >
                              <Box
                                 {...cmykDropHandlers}
                                 sx={{
                                    boxSizing: 'border-box',
                                    border: '1px dashed #c6a563',
                                    borderRadius: 1,
                                    p: 2,
                                    textAlign: 'center',
                                    color: '#000000',
                                    backgroundColor: '#e9f0ff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: 116,
                                    overflow: 'hidden',
                                    opacity: isUploadDisabled ? 0.5 : 1,
                                    pointerEvents: isUploadDisabled ? 'none' : 'auto',
                                    transition:
                                       'border-color 0.15s ease, border-style 0.15s ease, background-color 0.15s ease',
                                    ...(cmykDropActive && !isUploadDisabled
                                       ? {
                                            borderStyle: 'solid',
                                            borderColor: '#b28f4e',
                                            backgroundColor: '#dce8ff',
                                         }
                                       : {}),
                                 }}
                              >
                                 {cmykFile ? (
                                    <Typography variant='body2' noWrap sx={{ width: '100%' }}>
                                       {truncateForDisplay(cmykFileName)}
                                    </Typography>
                                 ) : (
                                    <UploadDropPlaceholder detail='CMYK design PDF' />
                                 )}
                              </Box>
                           </Tooltip>
                           <input
                              type='file'
                              accept='.pdf'
                              hidden
                              disabled={isUploadDisabled}
                              onChange={handleFileChange(setCmykFile, setCmykFileName)}
                           />
                        </Box>
                     </Box>

                     <Box>
                        <Typography variant='subtitle1' fontWeight={600} mb={2}>
                           Upload embellishment layer PDF
                        </Typography>
                        <Box
                           component='label'
                           sx={{ display: 'block', cursor: isUploadDisabled ? 'not-allowed' : 'pointer' }}
                        >
                           <Tooltip
                              title={
                                 isUploadDisabled
                                    ? 'Account already connected from another device.'
                                    : scodixFile
                                      ? scodixFileName
                                      : ''
                              }
                              placement='top'
                              arrow
                           >
                              <Box
                                 {...scodixDropHandlers}
                                 sx={{
                                    boxSizing: 'border-box',
                                    border: '1px dashed #c6a563',
                                    borderRadius: 1,
                                    p: 2,
                                    textAlign: 'center',
                                    color: '#000000',
                                    backgroundColor: '#e9f0ff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: 116,
                                    overflow: 'hidden',
                                    opacity: isUploadDisabled ? 0.5 : 1,
                                    pointerEvents: isUploadDisabled ? 'none' : 'auto',
                                    transition:
                                       'border-color 0.15s ease, border-style 0.15s ease, background-color 0.15s ease',
                                    ...(scodixDropActive && !isUploadDisabled
                                       ? {
                                            borderStyle: 'solid',
                                            borderColor: '#b28f4e',
                                            backgroundColor: '#dce8ff',
                                         }
                                       : {}),
                                 }}
                              >
                                 {scodixFile ? (
                                    <Typography variant='body2' noWrap sx={{ width: '100%' }}>
                                       {truncateForDisplay(scodixFileName)}
                                    </Typography>
                                 ) : (
                                    <UploadDropPlaceholder detail='Embellishment layer PDF' />
                                 )}
                              </Box>
                           </Tooltip>
                           <input
                              type='file'
                              accept='.pdf'
                              hidden
                              disabled={isUploadDisabled}
                              onChange={handleFileChange(setScodixFile, setScodixFileName)}
                           />
                        </Box>
                     </Box>
                  </Box>
               ) : (
                  <Box>
                     <Typography variant='subtitle1' fontWeight={600} mb={2}>
                        Upload multi-layer design PDF
                     </Typography>
                     <Box
                        component='label'
                        sx={{ display: 'block', cursor: isUploadDisabled ? 'not-allowed' : 'pointer' }}
                     >
                        <Tooltip
                           title={
                              isUploadDisabled
                                 ? 'Account already connected from another device.'
                                 : combinedFile
                                   ? combinedFileName
                                   : ''
                           }
                           placement='top'
                           arrow
                        >
                           <Box
                              {...combinedDropHandlers}
                              sx={{
                                 boxSizing: 'border-box',
                                 border: '1px dashed #c6a563',
                                 borderRadius: 1,
                                 p: 2,
                                 textAlign: 'center',
                                 color: '#000000',
                                 backgroundColor: '#e9f0ff',
                                 display: 'flex',
                                 alignItems: 'center',
                                 justifyContent: 'center',
                                 minHeight: 116,
                                 overflow: 'hidden',
                                 opacity: isUploadDisabled ? 0.5 : 1,
                                 pointerEvents: isUploadDisabled ? 'none' : 'auto',
                                 transition:
                                    'border-color 0.15s ease, border-style 0.15s ease, background-color 0.15s ease',
                                 ...(combinedDropActive && !isUploadDisabled
                                    ? {
                                         borderStyle: 'solid',
                                         borderColor: '#b28f4e',
                                         backgroundColor: '#dce8ff',
                                      }
                                    : {}),
                              }}
                           >
                              {combinedFile ? (
                                 <Typography variant='body2' noWrap sx={{ width: '100%' }}>
                                    {truncateForDisplay(combinedFileName)}
                                 </Typography>
                              ) : (
                                 <UploadDropPlaceholder detail='PDF with CMYK and embellishment layers' />
                              )}
                           </Box>
                        </Tooltip>
                        <input
                           type='file'
                           accept='.pdf'
                           hidden
                           disabled={isUploadDisabled}
                           onChange={handleFileChange(setCombinedFile, setCombinedFileName)}
                        />
                     </Box>
                  </Box>
               )}

               <Box
                  sx={{
                     mt: 3,
                     display: 'flex',
                     flexDirection: { xs: 'column', md: 'row' },
                     alignItems: { md: 'center' },
                     gap: 3,
                  }}
               >
                  <FormControl
                     component='fieldset'
                     sx={{ width: { xs: '100%', md: 'auto' }, flexGrow: 1 }}
                     disabled={!isCoatingSelectionEnabled}
                  >
                     <FormLabel component='legend'>Select Embellishment type</FormLabel>
                     <RadioGroup
                        row
                        value={selectedCoatType}
                        onChange={handleCoatTypeChange}
                        sx={{ flexWrap: 'wrap' }}
                     >
                        <FormControlLabel value='gold' control={<Radio />} label='Foil' />
                        <FormControlLabel value='varnish' control={<Radio />} label='Sense' />
                     </RadioGroup>
                  </FormControl>

                  <FormControl
                     component='fieldset'
                     sx={{
                        width: { xs: '100%', md: 'auto' },
                        ml: { md: 'auto' },
                     }}
                     disabled={!isCoatingSelectionEnabled}
                  >
                     <FormLabel component='legend'>Select Effect Height</FormLabel>
                     <RadioGroup row value={selectedThickness} onChange={handleThicknessChange}>
                        <FormControlLabel value='min' control={<Radio />} label='Min' />
                        <FormControlLabel value='mid' control={<Radio />} label='Mid' />
                        <FormControlLabel value='max' control={<Radio />} label='Max' />
                     </RadioGroup>
                  </FormControl>
               </Box>

               {filesReady?.scodixFileData && filesReady.scodixFileData.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                     <FormControl fullWidth>
                        <InputLabel id='select-layer-label'>Select Layer</InputLabel>
                        <Select
                           labelId='select-layer-label'
                           value={validRadioValue}
                           label='Select Layer'
                           onChange={(event) => setSelectedLayer(event.target.value)}
                        >
                           {filesReady.scodixFileData.map((layer, index) => (
                              <MenuItem key={`${layer.Layer}-${layer.Uri}-${index}`} value={layer.Layer}>
                                 {layer.Layer}
                              </MenuItem>
                           ))}
                        </Select>
                     </FormControl>
                  </Box>
               )}

               {progressUpdate && (
                  <Box
                     sx={{
                        mt: 3,
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        overflow: 'hidden',
                     }}
                  >
                     <Typography
                        variant='body2'
                        sx={{
                           lineHeight: 1.3,
                           height: '2.6em',
                           display: '-webkit-box',
                           WebkitLineClamp: 2,
                           WebkitBoxOrient: 'vertical',
                           overflow: 'hidden',
                           textOverflow: 'ellipsis',
                           wordBreak: 'break-word',
                           flexShrink: 0,
                        }}
                     >
                        {progressUpdate.message}
                     </Typography>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                        <LinearProgress
                           variant='determinate'
                           value={progressUpdate.progressPercentage}
                           sx={{ flex: 1, minWidth: 0, height: 6, borderRadius: 1 }}
                        />
                        <Typography variant='caption' color='text.secondary' sx={{ minWidth: 36 }}>
                           {`${Math.round(progressUpdate.progressPercentage)}%`}
                        </Typography>
                     </Box>
                  </Box>
               )}

               {(!filesReady || !hasNoErrors) && (
                  <LoadingButton
                     onClick={handleProcess}
                     disabled={!filesReadyToProcess || isProcessing || isUploadDisabled}
                     fullWidth
                     variant='contained'
                     sx={{
                        mt: 3,
                        backgroundColor: '#c3a05f',
                        color: '#ffffff',
                        borderRadius: '8px',
                        height: 48,
                        '&:hover': { backgroundColor: '#b28f4e' },
                     }}
                  >
                     {isProcessing ? 'Processing...' : 'Start Visualize'}
                  </LoadingButton>
               )}

               {filesReady && hasNoErrors && (
                  <Button
                     onClick={handleShowPreview}
                     disabled={!validRadioValue}
                     fullWidth
                     variant='contained'
                     sx={{
                        mt: 2,
                        backgroundColor: '#c3a05f',
                        color: '#ffffff',
                        borderRadius: '8px',
                        height: 48,
                        '&:hover': { backgroundColor: '#b28f4e' },
                     }}
                  >
                     Show Preview
                  </Button>
               )}

               {/* Progress UI intentionally hidden for now */}
            </CardContent>
         </Card>
      </Container>
   );
};

export default JobSelection;
